import {
  FINDING_STATUS_LABELS,
  HANDOVER_STATUS_LABELS,
  HANDOVER_TYPE_LABELS,
  type HandoverDocument,
  type HandoverPhoto,
  type InspectionItem,
  type Progress,
  groupInspection,
  isComplete,
  progressOf,
} from '@sivan/shared';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Value } from '../master-data/Value';
import { navigate, navigateBack, useHash } from '../routing';
import { Icon, type IconName } from '../ui/Icon';
import { ProgressRing } from '../ui/ProgressRing';
import { ResultSegment } from '../ui/ResultSegment';
import { FutureDateWarning, isFuture, relativeDay } from './DateSheet';
import type { Draft, DraftFinding } from './handover-draft';
import { formatDate } from './handover-draft';
import {
  type HandoverLocation,
  type HandoverStep,
  OVERVIEW,
  formatStep,
  parseStep,
} from './location';
import { INSPECTION_SEGMENTS, type InspectionChoice, chosen } from './results';
import type { Review } from './review';

// The handover on a phone (Design Gate 1.2): one compact graphite row on top, the work in the
// middle, one recommended action at the bottom within thumb reach. Steps (overview, a room, the
// general checks, the reinspection, findings and meters, the summary) are kept in the URL hash, so
// the phone's back button steps back through them and a refresh stays in place. The editor owns
// the draft and its saving (SAFE-DRAFT); this component only presents it.

/** The save state as the header shows it. */
export interface SaveView {
  readonly kind: 'saved' | 'dirty' | 'invalid' | 'saving' | 'offline' | 'error' | 'merging';
  /** Short text for the header. */
  readonly label: string;
  /** What happened and what to do (offline, failed or incomplete saves). */
  readonly detail: string | null;
}

type RoomTone = 'idle' | 'progress' | 'done' | 'defect';

interface Room {
  readonly areaId: string;
  readonly area: string;
  readonly progress: Progress;
  readonly tone: RoomTone;
  readonly text: string;
  readonly left: number;
}

const defectText = (n: number) => (n === 1 ? 'ליקוי אחד' : `${String(n)} ליקויים`);
const itemsText = (n: number) => (n === 1 ? 'פריט אחד' : `${String(n)} פריטים`);

function roomOf(areaId: string, area: string, progress: Progress): Room {
  const left = progress.total - progress.checked;
  const done = isComplete(progress);
  const tone: RoomTone =
    progress.defects > 0 ? 'defect' : done ? 'done' : progress.checked > 0 ? 'progress' : 'idle';
  const text =
    progress.defects > 0
      ? `${done ? 'הושלם · ' : ''}${defectText(progress.defects)}${left > 0 ? ` · נותרו ${String(left)}` : ''}`
      : done
        ? 'הושלם'
        : progress.checked > 0
          ? `בתהליך · נותרו ${String(left)}`
          : 'טרם התחיל';
  return { areaId, area, progress, tone, text, left };
}

const TONE_ICON: Readonly<Record<RoomTone, IconName>> = {
  idle: 'circle',
  progress: 'clock',
  done: 'check',
  defect: 'alert',
};

function StateChip({ tone, text }: { readonly tone: RoomTone; readonly text: string }) {
  return (
    <span className={`state state--${tone}`}>
      <Icon name={TONE_ICON[tone]} />
      {text}
    </span>
  );
}

/** Where to go next, and why. */
interface Next {
  readonly step: HandoverStep;
  readonly label: string;
  readonly reason: string;
}

export interface MobileHandoverProps {
  readonly doc: HandoverDocument;
  readonly draft: Draft;
  readonly review: Review;
  readonly location: HandoverLocation;
  readonly readOnly: boolean;
  readonly dirty: boolean;
  readonly save: SaveView;
  /** What happened to unsaved changes (restored, merged). */
  readonly banner: string | null;
  /** The result of the last action (for example, items left unmarked by "הכל תקין"). */
  readonly notice: string | null;
  readonly busy: boolean;
  readonly urlOf: (photo: HandoverPhoto) => string;
  /** The general checks, reinspection and findings sections, as the editor renders them. */
  readonly checksView: ReactNode;
  /** Signing and closing (or the closed handover's outcome), at the end of the summary. */
  readonly closingView: ReactNode;
  readonly reinspectionView: ReactNode;
  readonly findingsView: ReactNode;
  /** The dialogs (finding, closing, reinspection details, merge), shared with the desktop. */
  readonly dialogs: ReactNode;
  readonly onLeave: () => void;
  readonly onDismissBanner: () => void;
  readonly onRetry: () => void;
  readonly onDiscard: () => void;
  /** Opens "מחיקת טיוטה"; null when the user may not discard this draft. */
  readonly onDiscardDraft: (() => void) | null;
  /** Opens "חדרים בדירה"; null when the rooms cannot be changed here. */
  readonly onEditRooms: (() => void) | null;
  readonly onDate: () => void;
  readonly onStartInspection: () => void;
  readonly onMark: (item: InspectionItem, status: InspectionChoice) => void;
  readonly onAllOk: (items: readonly InspectionItem[], area: string, category: string) => void;
  readonly onAddFinding: (item: InspectionItem) => void;
  /** "צילום ליקוי": a finding in this room that starts from the camera; null when read-only. */
  readonly onPhotoFinding:
    | ((room: { readonly areaId: string; readonly area: string }, files: readonly File[]) => void)
    | null;
  readonly onEditFinding: (key: string) => void;
  readonly onCloseFinding: (finding: DraftFinding) => void;
  readonly onReopenFinding: (finding: DraftFinding) => void;
}

export function MobileHandover(props: MobileHandoverProps) {
  const { doc, draft, review, location, save } = props;
  const step = parseStep(useHash());
  const items = doc.inspection.items.map((i) => ({
    ...i,
    status: draft.results[i.id] ?? i.status,
  }));
  const areas = groupInspection(items);
  const rooms = areas.map((a) =>
    roomOf(a.areaId, a.area, progressOf(a.categories.flatMap((c) => c.items))),
  );
  const room = step.kind === 'room' ? areas.find((a) => a.areaId === step.areaId) : undefined;

  const go = (target: HandoverStep) => {
    navigate(formatStep(location, target), 'push');
  };
  const back = () => {
    if (step.kind === 'overview') props.onLeave();
    else navigateBack(formatStep(location, OVERVIEW));
  };

  /** The recommended next step after `from` (a room index), or from the start. */
  const nextAfter = (from: number): Next | null => {
    const order = [...rooms.slice(from + 1), ...rooms.slice(0, Math.max(0, from + 1))];
    const open = order.find((r) => r.left > 0);
    if (open !== undefined)
      return {
        step: { kind: 'room', areaId: open.areaId },
        label: open.area,
        reason: open.progress.checked > 0 ? open.text : `${itemsText(open.left)} לבדיקה`,
      };
    if (review.checks.total > review.checks.checked)
      return {
        step: { kind: 'checks' },
        label: 'בדיקות ומסירה כללית',
        reason: `נותרו ${String(review.checks.total - review.checks.checked)}`,
      };
    if (review.awaiting.length > 0)
      return {
        step: { kind: 'reinspection' },
        label: 'בדיקה חוזרת',
        reason: `ממתינים ${String(review.awaiting.length)}`,
      };
    return null;
  };

  // A new step starts at the top, with its title focused (screen readers announce where they are).
  const title = useRef<HTMLHeadingElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const stepKey = step.kind === 'room' ? `room:${step.areaId}` : step.kind;
  const first = useRef(true);
  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
    if (first.current) {
      first.current = false;
      return;
    }
    title.current?.focus();
  }, [stepKey]);

  const headTitle =
    step.kind === 'room'
      ? (room?.area ?? 'חדר')
      : step.kind === 'checks'
        ? 'בדיקות ומסירה כללית'
        : step.kind === 'reinspection'
          ? 'בדיקה חוזרת'
          : step.kind === 'findings'
            ? 'ליקויים ומונים'
            : step.kind === 'review'
              ? 'סיכום הבדיקה'
              : `דירה ${doc.apartment.apartmentNumber}`;
  const currentRoom = room === undefined ? undefined : rooms.find((r) => r.areaId === room.areaId);
  const headSub =
    step.kind === 'room' && currentRoom !== undefined
      ? `${String(currentRoom.progress.checked)}/${String(currentRoom.progress.total)} · ${currentRoom.text}`
      : step.kind === 'review'
        ? HANDOVER_STATUS_LABELS[doc.status]
        : `${HANDOVER_TYPE_LABELS[doc.type]} · ${doc.protocolNumber} · ${HANDOVER_STATUS_LABELS[doc.status]}${props.readOnly ? ' · צפייה בלבד' : ''}`;

  let body: ReactNode;
  let dock: ReactNode;
  if (step.kind === 'room' && room !== undefined) {
    const index = areas.indexOf(room);
    const own = rooms[index];
    const following = nextAfter(index);
    const roomDone = own?.left === 0;
    body = <RoomBody {...props} categories={room.categories} area={room.area} />;
    dock = (
      <div className="m-dock">
        {following === null ? (
          <button
            type="button"
            className="button button--primary m-dock__main"
            onClick={() => {
              go({ kind: 'review' });
            }}
          >
            <span>לסיכום הבדיקה</span>
            <Icon name="arrow-left" />
          </button>
        ) : (
          <button
            type="button"
            className={`button ${roomDone ? 'button--primary' : 'button--secondary'} m-dock__main`}
            onClick={() => {
              go(following.step);
            }}
          >
            <span>
              {following.step.kind === 'room' ? 'לחדר הבא' : 'הבא'}: {following.label}
            </span>
            <Icon name="arrow-left" />
          </button>
        )}
        <button
          type="button"
          className="button button--secondary m-dock__side"
          aria-label="כל החדרים"
          onClick={() => {
            navigateBack(formatStep(location, OVERVIEW));
          }}
        >
          <Icon name="list" />
        </button>
        {own !== undefined && own.left > 0 && (
          <p className="m-dock__hint">נותרו {itemsText(own.left)} בחדר הזה</p>
        )}
      </div>
    );
  } else if (step.kind === 'checks' || step.kind === 'reinspection' || step.kind === 'findings') {
    body =
      step.kind === 'checks'
        ? props.checksView
        : step.kind === 'reinspection'
          ? props.reinspectionView
          : props.findingsView;
    const following = nextAfter(rooms.length);
    const target =
      following !== null && following.step.kind !== step.kind
        ? following
        : { step: { kind: 'review' } as const, label: 'סיכום הבדיקה', reason: '' };
    dock = (
      <div className="m-dock m-dock--single">
        <button
          type="button"
          className="button button--primary m-dock__main"
          onClick={() => {
            go(target.step);
          }}
        >
          <span>{target.step.kind === 'review' ? 'לסיכום הבדיקה' : `הבא: ${target.label}`}</span>
          <Icon name="arrow-left" />
        </button>
      </div>
    );
  } else if (step.kind === 'review') {
    const following = nextAfter(-1);
    body = <ReviewBody {...props} rooms={rooms} areas={areas} go={go} />;
    dock = (
      <div className="m-dock m-dock--single">
        {following === null ? (
          <button
            type="button"
            className="button button--secondary m-dock__main"
            onClick={() => {
              navigateBack(formatStep(location, OVERVIEW));
            }}
          >
            <span>חזרה לדירה</span>
          </button>
        ) : (
          <button
            type="button"
            className="button button--primary m-dock__main"
            onClick={() => {
              go(following.step);
            }}
          >
            <span>להשלמה: {following.label}</span>
            <Icon name="arrow-left" />
          </button>
        )}
      </div>
    );
  } else {
    const following = nextAfter(-1);
    body = <OverviewBody {...props} rooms={rooms} go={go} />;
    dock = (
      <div className="m-dock m-dock--single">
        <button
          type="button"
          className="button button--primary m-dock__main"
          onClick={() => {
            go(following?.step ?? { kind: 'review' });
          }}
        >
          <span>
            {following === null
              ? 'לסיכום הבדיקה'
              : `${following.step.kind === 'room' && rooms.some((r) => r.progress.checked > 0) ? 'המשך' : 'התחלה'}: ${following.label}`}
          </span>
          <Icon name="arrow-left" />
        </button>
        {following !== null && <p className="m-dock__hint">הצעד המומלץ · {following.reason}</p>}
      </div>
    );
  }

  return (
    <div className={`m-handover m-handover--${doc.type}`}>
      <header className="m-head">
        <button
          type="button"
          className="m-head__back"
          aria-label={step.kind === 'overview' ? 'חזרה לדירה' : 'חזרה לחדרים'}
          onClick={back}
        >
          <Icon name="arrow-right" />
        </button>
        <div className="m-head__titles">
          <h1 className="m-head__title" ref={title} tabIndex={-1}>
            {headTitle}
          </h1>
          <p className="m-head__sub">{headSub}</p>
        </div>
        {!props.readOnly && <SaveIndicator save={save} />}
      </header>
      {!props.readOnly && save.detail !== null && (
        <div className={`m-alert m-alert--${save.kind}`} role="alert">
          <Icon name={save.kind === 'offline' ? 'cloud-off' : 'alert'} />
          <p>{save.detail}</p>
          {save.kind !== 'merging' && (
            <button type="button" className="button button--small" onClick={props.onRetry}>
              {save.kind === 'invalid' ? 'מה חסר?' : 'ניסיון שמירה חוזר'}
            </button>
          )}
        </div>
      )}
      {step.kind === 'room' && room !== undefined && (
        <RoomStrip
          rooms={rooms}
          current={room.areaId}
          onRoom={(areaId) => {
            navigate(formatStep(location, { kind: 'room', areaId }), 'replace');
          }}
        />
      )}
      <div className="m-scroll" ref={scroller}>
        <div className="m-pad">
          {props.banner !== null && (
            <div className="m-note" role="status">
              <p>{props.banner}</p>
              <button
                type="button"
                className="button button--small"
                onClick={props.onDismissBanner}
              >
                הבנתי
              </button>
            </div>
          )}
          {body}
        </div>
      </div>
      {dock}
      {props.dialogs}
    </div>
  );
}

/** The save state in the header: a check when everything is saved, words when it matters. */
function SaveIndicator({ save }: { readonly save: SaveView }) {
  // "נשמר" is spelled out for a moment after a save, then shrinks to the check.
  const [fresh, setFresh] = useState(false);
  const [last, setLast] = useState(save.kind);
  if (last !== save.kind) {
    setLast(save.kind);
    setFresh(save.kind === 'saved');
  }
  useEffect(() => {
    if (!fresh) return;
    const timer = window.setTimeout(() => {
      setFresh(false);
    }, 2500);
    return () => {
      window.clearTimeout(timer);
    };
  }, [fresh]);
  const icon: IconName =
    save.kind === 'saved'
      ? 'cloud-check'
      : save.kind === 'saving'
        ? 'refresh'
        : save.kind === 'offline'
          ? 'cloud-off'
          : save.kind === 'error' || save.kind === 'invalid' || save.kind === 'merging'
            ? 'alert'
            : 'clock';
  const compact = save.kind === 'saved' && !fresh;
  return (
    <p
      className={`m-save m-save--${save.kind}${compact ? ' m-save--compact' : ''}`}
      role="status"
      data-state={save.kind}
    >
      <Icon name={icon} />
      <span className={compact ? 'visually-hidden' : undefined}>{save.label}</span>
    </p>
  );
}

function RoomStrip({
  rooms,
  current,
  onRoom,
}: {
  readonly rooms: readonly Room[];
  readonly current: string;
  readonly onRoom: (areaId: string) => void;
}) {
  const strip = useRef<HTMLElement>(null);
  // The current room is brought into view without moving the keyboard's starting point.
  useEffect(() => {
    const bar = strip.current;
    const button = bar?.querySelector<HTMLElement>('[aria-current="true"]') ?? null;
    if (bar === null || button === null) return;
    const b = bar.getBoundingClientRect();
    const c = button.getBoundingClientRect();
    bar.scrollLeft += c.left + c.width / 2 - (b.left + b.width / 2);
  }, [current]);
  return (
    <nav className="m-strip" aria-label="מעבר בין חדרים" ref={strip}>
      {rooms.map((r) => (
        <button
          key={r.areaId}
          type="button"
          aria-current={r.areaId === current ? 'true' : undefined}
          aria-label={`${r.area}: ${r.text}`}
          onClick={() => {
            onRoom(r.areaId);
          }}
        >
          <Icon name={TONE_ICON[r.tone]} className={`mark--${r.tone}`} />
          {r.area}
        </button>
      ))}
    </nav>
  );
}

function OverviewBody({
  doc,
  draft,
  review,
  rooms,
  readOnly,
  dirty,
  busy,
  go,
  onDate,
  onDiscard,
  onDiscardDraft,
  onEditRooms,
  onStartInspection,
}: MobileHandoverProps & {
  readonly rooms: readonly Room[];
  readonly go: (step: HandoverStep) => void;
}) {
  const overall = progressOf(
    doc.inspection.items.map((i) => ({ status: draft.results[i.id] ?? i.status })),
  );
  const next = rooms.find((r) => r.left > 0);
  const a = doc.apartment;
  const spaces = [
    ...a.parking.map((s) => `חניה ${s.number}`),
    ...a.storage.map((s) => `מחסן ${s.number}`),
  ];
  const checksTone: RoomTone =
    review.checks.total === 0
      ? 'idle'
      : review.checks.checked === review.checks.total
        ? 'done'
        : review.checks.checked > 0
          ? 'progress'
          : 'idle';
  const reinspection = doc.reinspection;
  return (
    <>
      <section className="m-card m-who" aria-label="פרטי הדירה">
        <div>
          <p className="m-who__apt">
            דירה <Value text={a.apartmentNumber} />
          </p>
          <p className="m-meta">
            {doc.project.name} · בניין <Value text={a.building} />
            {a.floor !== '' && (
              <>
                {' '}
                · <Value text={a.floor} />
              </>
            )}
            {spaces.length > 0 && ` · ${spaces.join(' · ')}`}
          </p>
        </div>
        {overall.total > 0 && (
          <ProgressRing
            done={overall.checked}
            total={overall.total}
            size={52}
            label={`נבדקו ${String(overall.checked)} מתוך ${String(overall.total)} פריטים`}
          />
        )}
        <div className="m-who__facts">
          <button
            type="button"
            className="m-fact"
            disabled={readOnly}
            aria-label={`תאריך מסירה בפועל: ${draft.handoverDate === '' ? 'לא נקבע' : formatDate(draft.handoverDate)}${readOnly ? '' : '. שינוי'}`}
            onClick={onDate}
          >
            <small>תאריך מסירה בפועל</small>
            <b>
              {draft.handoverDate === '' ? (
                'לא נקבע'
              ) : (
                <>
                  {relativeDay(draft.handoverDate) !== '' && `${relativeDay(draft.handoverDate)} `}
                  <Value text={formatDate(draft.handoverDate)} />
                </>
              )}
              {!readOnly && <Icon name="calendar" />}
            </b>
          </button>
          <p className="m-fact">
            <small>מועד ביומן</small>
            <b>
              {doc.appointment === null ? (
                'לא נקבע'
              ) : (
                <AppointmentText at={doc.appointment.startsAt} />
              )}
            </b>
          </p>
        </div>
        {isFuture(draft.handoverDate) && <FutureDateWarning />}
      </section>

      {reinspection !== null && (
        <button
          type="button"
          className="m-card m-row"
          onClick={() => {
            go({ kind: 'reinspection' });
          }}
        >
          <span className="m-row__icon">
            <Icon name="refresh" />
          </span>
          <span className="m-row__text">
            <b>ליקויים לבדיקה חוזרת</b>
            {reinspection.initial === null ? (
              <StateChip tone="idle" text="לא מקושרת למסירה הראשונית" />
            ) : review.awaiting.length > 0 ? (
              <StateChip tone="progress" text={`ממתינים ${String(review.awaiting.length)}`} />
            ) : (
              <StateChip
                tone={review.stillDefective > 0 ? 'defect' : 'done'}
                text={
                  review.stillDefective > 0
                    ? `${String(review.stillDefective)} עדיין לא תקין`
                    : 'הושלם'
                }
              />
            )}
          </span>
          <span className="m-row__count">{reinspection.items.length}</span>
          <Icon name="chevron-left" />
        </button>
      )}

      {doc.inspection.items.length === 0 && doc.inspection.templateRevision === null ? (
        <section className="m-card m-empty" aria-label="בדיקת הדירה">
          <h2>בדיקת הדירה</h2>
          <p className="hint">
            לטיוטה זו עדיין אין רשימת בדיקה מסודרת לפי חדרים. הליקויים שנרשמו בה נשמרים כפי שהם.
          </p>
          {!readOnly && (
            <>
              <button
                type="button"
                className="button button--primary"
                disabled={dirty || busy}
                onClick={onStartInspection}
              >
                {busy ? 'מתחיל…' : 'התחלת בדיקה לפי תבנית הפרויקט'}
              </button>
              {dirty && <p className="hint">יש לשמור את הטיוטה לפני התחלת הבדיקה.</p>}
            </>
          )}
        </section>
      ) : (
        <section aria-labelledby="m-rooms-title">
          <h2 className="m-label" id="m-rooms-title">
            <span>חדרים</span>
            <span className="m-meta">
              {overall.checked}/{overall.total}
            </span>
          </h2>
          <ul className="m-card m-list">
            {rooms.map((r) => (
              <li key={r.areaId}>
                <button
                  type="button"
                  className={`m-row${r.areaId === next?.areaId ? ' m-row--next' : ''}`}
                  onClick={() => {
                    go({ kind: 'room', areaId: r.areaId });
                  }}
                >
                  <ProgressRing
                    done={r.progress.checked}
                    total={r.progress.total}
                    size={32}
                    tone={r.tone === 'defect' && r.left === 0 ? 'defect' : undefined}
                  />
                  <span className="m-row__text">
                    <b>
                      {r.area}
                      {r.areaId === next?.areaId && (
                        <span className="visually-hidden"> (החדר המומלץ)</span>
                      )}
                    </b>
                    <StateChip tone={r.tone} text={r.text} />
                  </span>
                  <span className="m-row__count">
                    {r.progress.checked}/{r.progress.total}
                  </span>
                  <Icon name="chevron-left" />
                </button>
              </li>
            ))}
          </ul>
          {onEditRooms !== null && (
            <button type="button" className="button m-rooms-edit" onClick={onEditRooms}>
              <Icon name="layers" />
              חדרים בדירה: הוספה או הסרה
            </button>
          )}
        </section>
      )}

      <ul className="m-card m-list" aria-label="עוד בדיקה">
        <li>
          <button
            type="button"
            className="m-row"
            onClick={() => {
              go({ kind: 'checks' });
            }}
          >
            <ProgressRing done={review.checks.checked} total={review.checks.total} size={32} />
            <span className="m-row__text">
              <b>בדיקות ומסירה כללית</b>
              <StateChip
                tone={checksTone}
                text={
                  review.checks.total === 0
                    ? 'אין בדיקות'
                    : checksTone === 'done'
                      ? 'הושלם'
                      : checksTone === 'idle'
                        ? 'טרם התחיל'
                        : `נותרו ${String(review.checks.total - review.checks.checked)}`
                }
              />
            </span>
            <span className="m-row__count">
              {review.checks.checked}/{review.checks.total}
            </span>
            <Icon name="chevron-left" />
          </button>
        </li>
        <li>
          <button
            type="button"
            className="m-row"
            onClick={() => {
              go({ kind: 'findings' });
            }}
          >
            <span className="m-row__icon">
              <Icon name="camera" />
            </span>
            <span className="m-row__text">
              <b>ליקויים ומונים</b>
              <span className="m-meta">
                {draft.findings.length === 0
                  ? 'לא נרשמו ליקויים'
                  : draft.findings.length === 1
                    ? 'ליקוי אחד'
                    : `${String(draft.findings.length)} ליקויים`}
                {' · '}
                {draft.meters.length === 0
                  ? 'ללא קריאת מונים'
                  : `${String(draft.meters.length)} מונים`}
              </span>
            </span>
            <span />
            <Icon name="chevron-left" />
          </button>
        </li>
        <li>
          <button
            type="button"
            className="m-row"
            onClick={() => {
              go({ kind: 'review' });
            }}
          >
            <span className="m-row__icon">
              <Icon name="clipboard" />
            </span>
            <span className="m-row__text">
              <b>סיכום ובדיקת שלמות</b>
              <span className="m-meta">
                {review.complete
                  ? 'הכול נבדק'
                  : `${String(review.incomplete.length + review.missing.length + review.awaiting.length)} לטיפול`}
                {' · '}
                {review.openFindings === 1
                  ? 'ליקוי פתוח אחד'
                  : `${String(review.openFindings)} ליקויים פתוחים`}
              </span>
            </span>
            <span />
            <Icon name="chevron-left" />
          </button>
        </li>
      </ul>

      {!readOnly && dirty && (
        <button type="button" className="button button--ghost m-discard" onClick={onDiscard}>
          <Icon name="undo" />
          ביטול שינויים שלא נשמרו
        </button>
      )}
      {/* Away from the dock and the room buttons, so that it is not tapped by mistake. */}
      {onDiscardDraft !== null && (
        <button
          type="button"
          className="button button--danger-quiet m-discard-draft"
          onClick={onDiscardDraft}
        >
          מחיקת הטיוטה
        </button>
      )}
    </>
  );
}

/** "היום 09:00" or "15/10/2026 09:00" for an appointment, in Israel time. */
function AppointmentText({ at }: { readonly at: string }) {
  const instant = new Date(at);
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem' }).format(instant);
  const time = new Intl.DateTimeFormat('he-IL', {
    timeZone: 'Asia/Jerusalem',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(instant);
  const relative = relativeDay(day);
  return (
    <>
      {relative === '' ? <Value text={formatDate(day)} /> : relative} <Value text={time} />
    </>
  );
}

function RoomBody({
  draft,
  readOnly,
  notice,
  urlOf,
  categories,
  area,
  onMark,
  onAllOk,
  onAddFinding,
  onPhotoFinding,
  onEditFinding,
  onCloseFinding,
  onReopenFinding,
}: MobileHandoverProps & {
  readonly area: string;
  readonly categories: readonly {
    readonly workCategoryId: string;
    readonly workCategory: string;
    readonly items: readonly InspectionItem[];
  }[];
}) {
  const areaId = categories[0]?.items[0]?.areaId;
  return (
    <>
      {notice !== null && (
        <p className="m-note" role="status">
          {notice}
        </p>
      )}
      {onPhotoFinding !== null && areaId !== undefined && (
        <PhotoFindingButton
          onFiles={(files) => {
            onPhotoFinding({ areaId, area }, files);
          }}
        />
      )}
      {categories.map((category) => {
        const p = progressOf(category.items);
        const done = isComplete(p);
        return (
          <section
            key={category.workCategoryId}
            className="m-card m-category"
            aria-label={category.workCategory}
          >
            <header className="m-category__head">
              <h2>
                {category.workCategory}{' '}
                <span className="m-meta">
                  {p.checked}/{p.total}
                </span>
              </h2>
              {!readOnly && !done && (
                <button
                  type="button"
                  className="button button--ghost button--small"
                  aria-label={`הכל תקין: ${category.workCategory} ב${area}`}
                  onClick={() => {
                    onAllOk(category.items, area, category.workCategory);
                  }}
                >
                  <Icon name="check" />
                  הכל תקין
                </button>
              )}
              {done && <StateChip tone={p.defects > 0 ? 'defect' : 'done'} text="הושלם" />}
            </header>
            <ul className="m-items">
              {category.items.map((item) => {
                const linked = draft.findings.filter((f) => f.inspectionItemId === item.id);
                return (
                  <li
                    key={item.id}
                    className={`m-item m-item--${item.status}`}
                    aria-label={item.name}
                  >
                    <p className="m-item__name">{item.name}</p>
                    <ResultSegment
                      label={`תוצאת בדיקה: ${item.name}`}
                      segments={INSPECTION_SEGMENTS}
                      value={chosen(item.status)}
                      disabled={readOnly}
                      onChoose={(status) => {
                        onMark(item, status);
                      }}
                    />
                    {linked.map((f) => (
                      <FindingRow
                        key={f.key}
                        finding={f}
                        readOnly={readOnly}
                        urlOf={urlOf}
                        onEdit={() => {
                          onEditFinding(f.key);
                        }}
                        onClose={() => {
                          onCloseFinding(f);
                        }}
                        onReopen={() => {
                          onReopenFinding(f);
                        }}
                      />
                    ))}
                    {!readOnly && item.status === 'defect' && (
                      <button
                        type="button"
                        className="button button--ghost button--small m-item__more"
                        aria-label={`ליקוי נוסף: ${item.name}`}
                        onClick={() => {
                          onAddFinding(item);
                        }}
                      >
                        <Icon name="plus" />
                        ליקוי נוסף
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </>
  );
}

function FindingRow({
  finding,
  readOnly,
  urlOf,
  onEdit,
  onClose,
  onReopen,
}: {
  readonly finding: DraftFinding;
  readonly readOnly: boolean;
  readonly urlOf: (photo: HandoverPhoto) => string;
  readonly onEdit: () => void;
  readonly onClose: () => void;
  readonly onReopen: () => void;
}) {
  const photo = finding.photos[0];
  return (
    <div className={`m-finding m-finding--${finding.status}`}>
      {photo === undefined ? (
        <Icon name={finding.status === 'open' ? 'alert' : 'check'} className="m-finding__mark" />
      ) : (
        <img
          className="m-finding__thumb"
          src={urlOf(photo)}
          alt=""
          width={40}
          height={40}
          loading="lazy"
        />
      )}
      <p className="m-finding__text">
        {finding.status !== 'open' && (
          <span className={`chip chip--finding-${finding.status}`}>
            {FINDING_STATUS_LABELS[finding.status]}
          </span>
        )}{' '}
        {finding.description}
        {finding.contractor.trim() !== '' && (
          <span className="m-meta"> · {finding.contractor}</span>
        )}
        {finding.photos.length > 1 && (
          <span className="m-meta"> · {finding.photos.length} תמונות</span>
        )}
      </p>
      {!readOnly && (
        <span className="m-finding__actions">
          <button
            type="button"
            className="icon-button"
            aria-label={`עריכת ליקוי ותמונות: ${finding.description}`}
            onClick={onEdit}
          >
            <Icon name="pencil" />
          </button>
          {finding.status === 'open' ? (
            <button
              type="button"
              className="icon-button"
              aria-label={`סגירת ליקוי: ${finding.description}`}
              onClick={onClose}
            >
              <Icon name="check" />
            </button>
          ) : (
            <button
              type="button"
              className="icon-button"
              aria-label={`פתיחה מחדש: ${finding.description}`}
              onClick={onReopen}
            >
              <Icon name="undo" />
            </button>
          )}
        </span>
      )}
    </div>
  );
}

function ReviewBody({
  doc,
  draft,
  review,
  rooms,
  areas,
  readOnly,
  go,
  onDate,
  onEditFinding,
  closingView,
}: MobileHandoverProps & {
  readonly rooms: readonly Room[];
  readonly areas: ReturnType<typeof groupInspection<InspectionItem>>;
  readonly go: (step: HandoverStep) => void;
}) {
  // Rooms and general checks; the reinspection has its own list below.
  const total = rooms.reduce((n, r) => n + r.progress.total, 0) + review.checks.total;
  const done = rooms.reduce((n, r) => n + r.progress.checked, 0) + review.checks.checked;
  const open = draft.findings.filter((f) => f.status === 'open');
  const itemRoom = (f: DraftFinding) =>
    doc.inspection.items.find((i) => i.id === f.inspectionItemId)?.areaId ?? null;
  const left = areas
    .map((a) => ({
      area: a,
      items: a.categories.flatMap((c) => c.items).filter((i) => i.status === 'unchecked'),
    }))
    .filter((r) => r.items.length > 0);
  const checksLeft = review.incomplete.filter((e) => e.target.kind === 'check');
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  // Progress in rooms, not in dozens of single checks.
  const roomsDone = rooms.filter((r) => r.left === 0).length;
  const withoutPhoto = draft.findings.filter(
    (f) => f.status !== 'withdrawn' && f.photos.length === 0,
  );
  const firstLeft = left[0];
  const next: { text: string; action: string; run: () => void } | null =
    firstLeft !== undefined
      ? {
          text: `${firstLeft.area.area}: ${itemsText(firstLeft.items.length)} עוד לא נבדקו`,
          action: `לחדר ${firstLeft.area.area}`,
          run: () => {
            go({ kind: 'room', areaId: firstLeft.area.areaId });
          },
        }
      : checksLeft.length > 0
        ? {
            text: 'בדיקות ומסירה כללית עוד לא הושלמו',
            action: 'לבדיקות הכלליות',
            run: () => {
              go({ kind: 'checks' });
            },
          }
        : review.awaiting.length > 0
          ? {
              text: 'ליקויים מהמסירה הראשונית ממתינים לבדיקה חוזרת',
              action: 'לבדיקה החוזרת',
              run: () => {
                go({ kind: 'reinspection' });
              },
            }
          : withoutPhoto[0] !== undefined
            ? {
                text: `לליקוי "${withoutPhoto[0].component}" חסרה תמונה`,
                action: 'להוספת תמונה',
                run: () => {
                  onEditFinding(withoutPhoto[0]?.key ?? '');
                },
              }
            : null;
  return (
    <>
      <section className="m-card m-score" aria-label="התקדמות">
        <ProgressRing done={roomsDone} total={rooms.length} size={64} />
        <div>
          <p className="m-score__title">
            {review.complete && withoutPhoto.length === 0 ? 'הבדיקה הושלמה' : 'מה נשאר לעשות'}
          </p>
          <p className="m-meta">
            {roomsDone} מתוך {rooms.length} חדרים הושלמו ({percent}%) ·{' '}
            {review.openFindings === 1
              ? 'ליקוי אחד נרשם'
              : `${String(review.openFindings)} ליקויים נרשמו`}
          </p>
        </div>
      </section>

      {next !== null ? (
        <section className="m-card m-next" aria-labelledby="m-review-next">
          <h2 className="m-sec" id="m-review-next">
            <Icon name="arrow-left" className="mark--progress" />
            הצעד הבא
          </h2>
          <p>{next.text}</p>
          <button type="button" className="button button--primary m-next__go" onClick={next.run}>
            {next.action}
          </button>
        </section>
      ) : (
        review.complete && (
          <section className="m-card m-next" aria-labelledby="m-review-next">
            <h2 className="m-sec" id="m-review-next">
              <Icon name="check" className="mark--ok" />
              הבדיקה הושלמה
            </h2>
            <p>נשארו החתימות. גללו לחלק "חתימה וסיום המסירה".</p>
          </section>
        )
      )}

      {withoutPhoto.length > 0 && (
        <section className="m-card" aria-labelledby="m-review-photo">
          <h2 className="m-sec m-sec--pending" id="m-review-photo">
            <Icon name="camera" />
            ליקויים בלי תמונה ({withoutPhoto.length})
          </h2>
          <ul className="m-todo">
            {withoutPhoto.map((f) => (
              <li key={f.key}>
                <button
                  type="button"
                  onClick={() => {
                    onEditFinding(f.key);
                  }}
                >
                  <Icon name="camera" className="mark--progress" />
                  <span>
                    <b>
                      {f.area} · {f.component}
                    </b>
                    <span className="m-meta">{f.description}</span>
                  </span>
                  <span className="m-todo__go">להוספת תמונה</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {open.length > 0 && (
        <section className="m-card" aria-labelledby="m-review-open">
          <h2 className="m-sec m-sec--defect" id="m-review-open">
            <Icon name="alert" />
            ליקויים שנרשמו ({open.length})
          </h2>
          <ul className="m-todo">
            {open.map((f) => {
              const areaId = itemRoom(f);
              return (
                <li key={f.key}>
                  <button
                    type="button"
                    onClick={() => {
                      go(areaId === null ? { kind: 'findings' } : { kind: 'room', areaId });
                    }}
                  >
                    <Icon name="alert" className="mark--defect" />
                    <span>
                      <b>
                        {f.area} · {f.component}
                      </b>
                      <span className="m-meta">{f.description}</span>
                    </span>
                    <span className="m-todo__go">{areaId === null ? 'לליקויים' : 'לחדר'}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {review.awaiting.length > 0 && (
        <section className="m-card" aria-labelledby="m-review-awaiting">
          <h2 className="m-sec" id="m-review-awaiting">
            <Icon name="refresh" className="mark--progress" />
            ממתינים לבדיקה חוזרת ({review.awaiting.length})
          </h2>
          <ul className="m-todo">
            {review.awaiting.map((e) => (
              <li key={e.key}>
                <button
                  type="button"
                  onClick={() => {
                    go({ kind: 'reinspection' });
                  }}
                >
                  <Icon name="clock" className="mark--progress" />
                  <span>
                    <b>{e.text}</b>
                  </span>
                  <span className="m-todo__go">לבדיקה</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(left.length > 0 || checksLeft.length > 0) && (
        <section className="m-card" aria-labelledby="m-review-left">
          <h2 className="m-sec" id="m-review-left">
            <Icon name="clock" className="mark--progress" />
            {left.length > 0 ? `חדרים שטרם נבדקו (${String(left.length)})` : 'בדיקות שטרם הושלמו'}
          </h2>
          <ul className="m-todo">
            {left.map(({ area, items }) => (
              <li key={area.areaId}>
                <button
                  type="button"
                  onClick={() => {
                    go({ kind: 'room', areaId: area.areaId });
                  }}
                >
                  <Icon name="circle" className="mark--idle" />
                  <span>
                    <b>{area.area}</b>
                    <span className="m-meta">
                      {items
                        .slice(0, 3)
                        .map((i) => i.name)
                        .join(', ')}
                      {items.length > 3 && ` ועוד ${String(items.length - 3)}`}
                    </span>
                  </span>
                  <span className="m-todo__go">{itemsText(items.length)}</span>
                </button>
              </li>
            ))}
            {checksLeft.length > 0 && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    go({ kind: 'checks' });
                  }}
                >
                  <Icon name="list" className="mark--idle" />
                  <span>
                    <b>בדיקות ומסירה כללית</b>
                    <span className="m-meta">
                      {checksLeft.map((e) => e.text.split(':')[0]).join(', ')}
                    </span>
                  </span>
                  <span className="m-todo__go">
                    {checksLeft.length === 1 ? 'בדיקה אחת' : `${String(checksLeft.length)} בדיקות`}
                  </span>
                </button>
              </li>
            )}
          </ul>
        </section>
      )}

      {review.missing.length > 0 && (
        <section className="m-card" aria-labelledby="m-review-missing">
          <h2 className="m-sec m-sec--pending" id="m-review-missing">
            <Icon name="alert" />
            מידע חסר ({review.missing.length})
          </h2>
          <ul className="m-todo">
            {review.missing.map((e) => (
              <li key={e.key}>
                <button
                  type="button"
                  onClick={() => {
                    if (e.target.kind === 'date') onDate();
                    else if (e.target.kind === 'check') go({ kind: 'checks' });
                    else if (e.target.kind === 'reinspection') go({ kind: 'reinspection' });
                    else if (e.target.kind === 'section')
                      go({
                        kind:
                          e.target.section === 'checks'
                            ? 'checks'
                            : e.target.section === 'reinspection'
                              ? 'reinspection'
                              : 'findings',
                      });
                  }}
                >
                  <Icon name="minus" className="mark--idle" />
                  <span>
                    <b>{e.text}</b>
                  </span>
                  <span className="m-todo__go">להשלמה</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="m-card m-date" aria-labelledby="m-review-date">
        <h2 className="m-sec" id="m-review-date">
          <Icon name="calendar" className="mark--progress" />
          תאריך מסירה בפועל
        </h2>
        <div className="m-date__row">
          <span>
            {draft.handoverDate === '' ? (
              'לא נקבע'
            ) : (
              <>
                {relativeDay(draft.handoverDate) !== '' && `${relativeDay(draft.handoverDate)} · `}
                <Value text={formatDate(draft.handoverDate)} />
              </>
            )}
          </span>
          {!readOnly && (
            <button
              type="button"
              className="button button--secondary button--small"
              onClick={onDate}
            >
              שינוי
            </button>
          )}
        </div>
        {isFuture(draft.handoverDate) && <FutureDateWarning />}
      </section>
      {closingView}
    </>
  );
}

/**
 * "צילום ליקוי": the camera first, then the finding form of this room with the photo uploading,
 * for a defect noticed before its checklist item is found (the photo is never lost while the
 * details are filled in).
 */
function PhotoFindingButton({ onFiles }: { readonly onFiles: (files: readonly File[]) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="m-photo-finding">
      <button
        type="button"
        className="button button--secondary m-photo-finding__button"
        onClick={() => input.current?.click()}
      >
        <Icon name="camera" />
        צילום ליקוי בחדר
      </button>
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        className="visually-hidden"
        tabIndex={-1}
        aria-label="צילום ליקוי"
        data-testid="photo-finding-input"
        onChange={(e) => {
          const files = [...(e.currentTarget.files ?? [])];
          e.currentTarget.value = '';
          if (files.length > 0) onFiles(files);
        }}
      />
    </div>
  );
}
